import { FC } from 'react';

import { Button, Modal } from '@purpur/library';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { useProductVariant } from '@telialt/core-eshop/src/hooks/queries/useProductVariant';
import { ESHOP_TEST_ID } from '@telialt/core-eshop/src/test/test-ids';
import { useLogErrorEshop } from '@telialt/utils/src/datadog/useLogError';

import { Z_INDEX_MODAL } from '../../../constants/constants';
import { GalleryContainer } from '../GalleryContainer';
import {
    GiftDescriptionSection,
    GiftNewPrice,
    GiftOldPrice,
    GiftPriceContainer,
    GiftTitle,
} from './GiftDetailsModal.styles';
import { useGiftDetailsModalControls } from './useGiftDetailsModalControls';

export const GiftDetailsModal: FC = () => {
    const { t } = useTranslations();
    const { isModalOpen, closeModal, modalData } = useGiftDetailsModalControls();

    const [{ gift, showOldPriceCrossed }, goBackDepth] = modalData || [{}, -1];
    const { productVariant, productVariantError } = useProductVariant(gift?.code, true);
    const productImageData = gift?.images || [];

    useLogErrorEshop(productVariantError);
    const closeModalCompletely = () => window.history.go(goBackDepth);

    const buttons = [
        <Button
            key="close-completely"
            variant="primary"
            onClick={closeModalCompletely}
            data-test={ESHOP_TEST_ID.MODAL_CLOSE_BTN}
        >
            {t['common.button.close']}
        </Button>,
        goBackDepth <= -2 && (
            <Button key="close" variant="secondary" onClick={closeModal} data-test={ESHOP_TEST_ID.MODAL_GO_BACK_BTN}>
                {t['eshop.product_page.gift_modal.go_back_button']}
            </Button>
        ),
    ].filter(Boolean);

    return (
        <Modal open={isModalOpen} onOpenChange={(open) => !open && closeModalCompletely()}>
            <Modal.Content
                title={gift?.name}
                actions={<>{buttons}</>}
                stickyButtons={true}
                closeButtonAriaLabel={t['common.button.close']}
                showCloseButton={true}
                data-test={ESHOP_TEST_ID.GIFT_DETAILS_MODAL}
                zIndex={Z_INDEX_MODAL}
            >
                <div>
                    <GalleryContainer
                        productImageData={productImageData}
                        isZoomEnabled={false}
                        isFloatingNavButtonsEnabled={true}
                    />
                </div>
                <GiftTitle tag="h1" variant="title-300">
                    {gift?.name}
                </GiftTitle>
                <GiftPriceContainer data-test={ESHOP_TEST_ID.GIFT_PRICE_BLOCK}>
                    {showOldPriceCrossed && <GiftOldPrice>{gift?.standardPrice?.formattedValue}</GiftOldPrice>}
                    <GiftNewPrice>{gift?.price?.formattedValue}</GiftNewPrice>
                </GiftPriceContainer>
                <GiftDescriptionSection productVariant={productVariant} isRetailsShown={false} />
            </Modal.Content>
        </Modal>
    );
};
